import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import apiUrl from "../Apiurl";
import stilePulsanti from "../interfaccia/stilePulsanti";
import UserAvatarBadge from "./UserAvatarBadge";

const SITES = [
  { key: "VIG", label: "Viglietti", color: "#2563eb" },
  { key: "BOT", label: "Bottero", color: "#dc2626" },
];

function buildSiteTree(categories) {
  const byId = new Map();
  const roots = [];
  categories.forEach((category) => {
    byId.set(Number(category.external_id), { ...category, children: [] });
  });
  byId.forEach((node) => {
    const parentId = Number(node.parent_external_id);
    if (Number.isFinite(parentId) && byId.has(parentId)) {
      byId.get(parentId).children.push(node);
    } else {
      roots.push(node);
    }
  });
  const sortNodes = (nodes) => {
    nodes.sort((a, b) =>
      String(a.path || a.label || "").localeCompare(
        String(b.path || b.label || ""),
      ),
    );
    nodes.forEach((node) => sortNodes(node.children));
  };
  sortNodes(roots);
  return roots;
}

function collectSiteNodeIds(nodes, result = []) {
  if (!Array.isArray(nodes)) {
    return result;
  }
  nodes.forEach((node) => {
    result.push(Number(node.external_id));
    collectSiteNodeIds(node.children, result);
  });
  return result;
}

function filterUnmappedTree(nodes, mappingByExternalId) {
  return nodes
    .map((node) => {
      const children = filterUnmappedTree(node.children, mappingByExternalId);
      const hasMapping = mappingByExternalId.has(Number(node.external_id));
      if (!hasMapping || children.length > 0) {
        return { ...node, children };
      }
      return null;
    })
    .filter(Boolean);
}

function MappingPill({ mapping }) {
  const label = mapping.ruleDescription || mapping.ruleKey || "Map #" + mapping.ruleId;
  return (
    <span
      className="dib br-pill white f7 pv1 ph2 ml1 mb1"
      style={{ backgroundColor: "#475569", lineHeight: 1.15 }}
      title={label + " - categoria #" + mapping.externalCategoryId}
    >
      {label}
      {mapping.isDefaultCandidate && <span className="ml1 b">default</span>}
    </span>
  );
}
function formatCondition(condition) {
  const source = condition?.source || "?";
  const field = condition?.field || condition?.path || "campo";
  const operator = String(condition?.operator || "").toUpperCase();
  const values = Array.isArray(condition?.values)
    ? condition.values.join(", ")
    : (condition?.value ?? "");
  return `${source}: ${field} ${operator} ${values}`;
}

function formatOutputLabel(output) {
  return (
    output?.sitePath || output?.siteLabel || output?.externalCategoryId || ""
  );
}

function MappingDetailCard({ mapping }) {
  const conditions = Array.isArray(mapping.conditions)
    ? mapping.conditions
    : [];
  const outputs = Array.isArray(mapping.outputs) ? mapping.outputs : [];
  return (
    <div className="pa3 mb3 br2 ba b--black-10 sfondoBiancoTema">
      <div className="flex items-start justify-between mb2">
        <div className="pr2">
          <div className="f6 b">
            {mapping.ruleDescription ||
              mapping.ruleKey ||
              "Map #" + mapping.ruleId}
          </div>
          <div className="f7 gray">
            Regola #{mapping.ruleId} · priorita {mapping.rulePriority}
            {mapping.ruleKey ? " · " + mapping.ruleKey : ""}
          </div>
        </div>
        <div className="flex items-center">
          {mapping.isDefaultCandidate && (
            <span
              className="dib br-pill white f7 pv1 ph2 mr2"
              style={{ backgroundColor: "var(--colTastoVerde, #137752)" }}
            >
              default
            </span>
          )}
          <UserAvatarBadge
            username={mapping.updatedBy}
            fallbackUsername={mapping.createdBy}
            size={26}
          />
        </div>
      </div>

      <div className="mb2">
        <div className="f7 ttu tracked gray mb1">Categoria selezionata</div>
        <div className="f7 b">
          {mapping.site} #{mapping.externalCategoryId} ·{" "}
          {formatOutputLabel(mapping)}
        </div>
        <div className="f7 gray">Output priority {mapping.outputPriority}</div>
      </div>

      <div className="mb2">
        <div className="f7 ttu tracked gray mb1">Requisiti</div>
        {conditions.length === 0 ? (
          <div className="f7 gray">Wildcard completa.</div>
        ) : (
          <div className="flex flex-wrap">
            {conditions.map((condition, index) => (
              <span
                key={index}
                className="dib br-pill ph2 pv1 mr2 mb2 f7 bg-near-white"
              >
                {formatCondition(condition)}
              </span>
            ))}
          </div>
        )}
      </div>

      {outputs.length > 1 && (
        <div>
          <div className="f7 ttu tracked gray mb1">
            Tutti gli output della regola
          </div>
          <div className="flex flex-wrap">
            {outputs.map((output) => (
              <span
                key={`${output.site}-${output.externalCategoryId}-${output.id}`}
                className="dib br-pill ph2 pv1 mr2 mb2 f7"
                style={{
                  backgroundColor: output.isDefaultCandidate
                    ? "var(--colTastoVerde, #137752)"
                    : "rgba(19, 119, 82, 0.08)",
                  color: output.isDefaultCandidate ? "#fff" : "inherit",
                }}
              >
                {output.site}: {formatOutputLabel(output)}
                {output.isDefaultCandidate ? " · default" : ""}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function SiteCategoryTree({
  nodes,
  expandedIds,
  selectedId,
  mappingByExternalId,
  onToggle,
  onSelect,
}) {
  if (!Array.isArray(nodes) || nodes.length === 0) {
    return <div className="pa3 sfondoBiancoTema">Nessuna categoria.</div>;
  }

  return (
    <div>
      {nodes.map((node) => {
        const externalId = Number(node.external_id);
        const hasChildren =
          Array.isArray(node.children) && node.children.length > 0;
        const expanded = expandedIds.has(externalId);
        const selected = Number(selectedId) === externalId;
        const mappings = mappingByExternalId.get(externalId) || [];
        return (
          <div key={externalId} className="mb1">
            <div
              className={
                "flex items-center pa2 pointer " +
                (selected ? "sfondoColMedio scrittaBianco" : "sfondoBiancoTema")
              }
              onClick={() => onSelect(node)}
            >
              <button
                type="button"
                className={`button-reset bn pointer mr2 flex items-center justify-center scrittaColAltScuro ${!hasChildren ? "o-0" : ""}`}
                style={{
                  color: hasChildren ? undefined : "transparent",
                  lineHeight: 1,
                  fontSize: "1.75rem",
                  padding: 0,
                  background: "transparent",
                  border: "none",
                }}
                onClick={(event) => {
                  event.stopPropagation();
                  if (hasChildren) onToggle(externalId);
                }}
                disabled={!hasChildren}
                title={expanded ? "Comprimi" : "Espandi"}
              >
                {hasChildren ? (expanded ? "▾" : "▸") : "·"}
              </button>
              <div
                className={"tl " + (selected ? "scrittaBianco" : "")}
                style={{ flex: "0 1 50%", minWidth: 0 }}
              >
                <span className="b">{node.label}</span>
                <span className="ml2 f7">#{externalId}</span>
                {Number(node.active) !== 1 && (
                  <span className="ml2 f7">disattiva</span>
                )}
              </div>
              <div
                className="tr"
                style={{
                  flex: "0 0 50%",
                  maxWidth: "50%",
                  pointerEvents: "none",
                }}
              >
                {mappings.map((mapping) => (
                  <MappingPill
                    key={
                      mapping.ruleId +
                      "-" +
                      mapping.site +
                      "-" +
                      mapping.externalCategoryId +
                      "-" +
                      mapping.id
                    }
                    mapping={mapping}
                  />
                ))}
              </div>
            </div>
            {hasChildren && expanded && (
              <div className="ml3 pl2 bl b--black-10">
                <SiteCategoryTree
                  nodes={node.children}
                  expandedIds={expandedIds}
                  selectedId={selectedId}
                  mappingByExternalId={mappingByExternalId}
                  onToggle={onToggle}
                  onSelect={onSelect}
                />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

const PrestashopCategoriesPage = ({ token, utente, temaColori }) => {
  const { buttonClassesGreen, buttonClassesWhite } = stilePulsanti;
  const canSync = Boolean(utente?.permessiAPI?.["update:prestashop"]);

  const [selectedSite, setSelectedSite] = useState("VIG");
  const [siteCategories, setSiteCategories] = useState([]);
  const [rules, setRules] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [expandedIds, setExpandedIds] = useState(() => new Set());
  const [onlyUnmapped, setOnlyUnmapped] = useState(false);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  const authHeaders = useMemo(
    () => ({ headers: { Authorization: "Bearer " + token } }),
    [token],
  );

  const mappingByExternalId = useMemo(() => {
    const result = new Map();
    rules
      .filter((rule) => rule.active)
      .forEach((rule) => {
        (Array.isArray(rule.outputs) ? rule.outputs : [])
          .filter((output) => output.site === selectedSite)
          .forEach((output) => {
            const externalId = Number(output.externalCategoryId);
            if (!Number.isFinite(externalId)) return;
            const mappings = result.get(externalId) || [];
            mappings.push({
              ...output,
              ruleId: rule.id,
              ruleKey: rule.ruleKey,
              ruleDescription: rule.description,
              rulePriority: rule.priority,
              conditions: rule.conditions,
              outputs: rule.outputs,
              outputPriority: output.priority,
              createdBy: rule.createdBy,
              updatedBy: rule.updatedBy,
            });
            result.set(externalId, mappings);
          });
      });
    return result;
  }, [rules, selectedSite]);

  const siteTree = useMemo(
    () => buildSiteTree(siteCategories),
    [siteCategories],
  );
  const visibleTree = useMemo(
    () =>
      onlyUnmapped
        ? filterUnmappedTree(siteTree, mappingByExternalId)
        : siteTree,
    [siteTree, mappingByExternalId, onlyUnmapped],
  );
  const selectedMappings = selectedCategory
    ? mappingByExternalId.get(Number(selectedCategory.external_id)) || []
    : [];

  const loadData = useCallback(async () => {
    if (!token) {
      return;
    }
    setLoading(true);
    setError("");
    try {
      const [siteResponse, rulesResponse] = await Promise.all([
        axios.get(
          apiUrl + "/prodotti/categories/site-categories?site=" + selectedSite,
          authHeaders,
        ),
        axios.get(apiUrl + "/prodotti/categories/attribute-rules", authHeaders),
      ]);
      const siteData = Array.isArray(siteResponse?.data?.data)
        ? siteResponse.data.data
        : [];
      setSiteCategories(siteData);
      setRules(
        Array.isArray(rulesResponse?.data?.data) ? rulesResponse.data.data : [],
      );
      setExpandedIds((prev) =>
        prev.size > 0
          ? prev
          : new Set(siteData.map((category) => Number(category.external_id))),
      );
    } catch (loadError) {
      setError(
        loadError?.response?.data?.error ||
          loadError.message ||
          "Errore caricamento categorie Prestashop",
      );
    } finally {
      setLoading(false);
    }
  }, [token, selectedSite, authHeaders]);

  useEffect(() => {
    setSelectedCategory(null);
    setExpandedIds(new Set());
  }, [selectedSite]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const syncSiteCategories = async () => {
    if (!token || !canSync) {
      return;
    }
    setSyncing(true);
    setError("");
    setMessage("");
    try {
      const response = await axios.post(
        apiUrl + "/prodotti/categories/site-categories/sync",
        { sites: SITES.map((site) => site.key) },
        authHeaders,
      );
      const results = Array.isArray(response?.data?.data) ? response.data.data : [];
      const summary = results
        .map((result) => `${result.site}: ${result.persistedRows}/${result.totalRows}, annullate ${result.deactivatedRows || 0}`)
        .join(" · ");
      setMessage(summary ? "Sync categorie completata: " + summary : "Sync categorie completata.");
      await loadData();
    } catch (syncError) {
      setError(
        syncError?.response?.data?.error ||
          syncError.message ||
          "Errore sync categorie Prestashop",
      );
    } finally {
      setSyncing(false);
    }
  };

  const toggleNode = (externalId) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      const numericId = Number(externalId);
      if (next.has(numericId)) {
        next.delete(numericId);
      } else {
        next.add(numericId);
      }
      return next;
    });
  };

  const selectedSiteConfig =
    SITES.find((site) => site.key === selectedSite) || SITES[0];

  return (
    <div className={"pa3 " + (temaColori || "")}>
      <div className="sfondoBiancoTema pa3 br3">
        <div className="flex items-center justify-between mb3">
          <div>
            <h2 className="ma0">Categorie Prestashop</h2>
            <div className="f6 mt1">
              Alberatura nativa sito e output delle map categorie attive.
            </div>
          </div>
          <div>
            <button
              type="button"
              className={buttonClassesWhite}
              onClick={loadData}
              disabled={loading || syncing}
            >
              Aggiorna
            </button>
            <button
              type="button"
              className={buttonClassesGreen + " ml2"}
              onClick={syncSiteCategories}
              disabled={!canSync || loading || syncing}
              title={canSync ? "Forza sync categorie VIG/BOT" : "Permesso update:prestashop necessario"}
            >
              {syncing ? "Sync..." : "Sync siti"}
            </button>
          </div>
        </div>

        {error && <div className="pa3 mb3 bg-washed-red dark-red">{error}</div>}
        {message && <div className="pa3 mb3 bg-washed-green dark-green">{message}</div>}
        {!canSync && <div className="pa3 mb3 bg-washed-yellow">Per forzare la sync serve update:prestashop.</div>}
        <div className="flex flex-wrap items-center justify-between mb3">
          <div>
            {SITES.map((site) => (
              <button
                key={site.key}
                type="button"
                className={
                  (selectedSite === site.key
                    ? buttonClassesGreen
                    : buttonClassesWhite) + " mr2"
                }
                onClick={() => setSelectedSite(site.key)}
              >
                {site.label}
              </button>
            ))}
          </div>
          <label className="mt2 mt0-l pointer">
            <input
              type="checkbox"
              checked={onlyUnmapped}
              onChange={(event) => setOnlyUnmapped(event.target.checked)}
            />{" "}
            Solo categorie senza map attive
          </label>
        </div>

        <div className="flex flex-wrap items-start">
          <div className="pa3 mb3 w-50">
            <div className="flex flex-wrap items-center justify-between mb3">
              <div>
                <h3 className="ma0">Albero {selectedSiteConfig.label}</h3>
                <div className="f6 mt1">
                  <span
                    className="dib br-100 mr1"
                    style={{
                      width: "12px",
                      height: "12px",
                      backgroundColor: selectedSiteConfig.color,
                    }}
                  />
                  Categorie Prestashop {selectedSite}
                </div>
              </div>
              <div className="mt2 mt0-l">
                <button
                  type="button"
                  className={buttonClassesWhite}
                  onClick={() =>
                    setExpandedIds(new Set(collectSiteNodeIds(visibleTree)))
                  }
                >
                  Espandi tutto
                </button>
                <button
                  type="button"
                  className={buttonClassesWhite + " ml2"}
                  onClick={() => setExpandedIds(new Set())}
                >
                  Chiudi tutto
                </button>
              </div>
            </div>
            <div style={{ maxHeight: "calc(100vh - 260px)", overflowY: "auto", paddingRight: "0.5rem" }}>
              {loading ? (
                <div className="pa3 sfondoBiancoTema">Caricamento...</div>
              ) : (
                <SiteCategoryTree
                  nodes={visibleTree}
                  expandedIds={expandedIds}
                  selectedId={selectedCategory?.external_id}
                  mappingByExternalId={mappingByExternalId}
                  onToggle={toggleNode}
                  onSelect={setSelectedCategory}
                />
              )}
            </div>
          </div>

          <div className="pa3 sfondoColChiaro mb3 w-50" style={{ maxHeight: "calc(100vh - 210px)", overflowY: "auto" }}>
            {selectedCategory ? (
              <>
                <div className="flex flex-wrap items-start justify-between mb3">
                  <div>
                    <h3 className="ma0">{selectedCategory.label}</h3>
                    <div className="f6 mt1">
                      {selectedSite} #{selectedCategory.external_id}
                      {selectedCategory.parent_external_id
                        ? " · padre #" + selectedCategory.parent_external_id
                        : ""}
                    </div>
                    {selectedCategory.path && (
                      <div className="mt2 f6">{selectedCategory.path}</div>
                    )}
                  </div>
                </div>

                <h4 className="mt0">Map categorie attive</h4>
                {selectedMappings.length === 0 ? (
                  <div className="f6 mb3">
                    Nessuna map attiva usa questa categoria come output.
                  </div>
                ) : (
                  <div className="mb3">
                    {selectedMappings.map((mapping) => (
                      <MappingDetailCard
                        key={
                          mapping.ruleId +
                          "-" +
                          mapping.site +
                          "-" +
                          mapping.externalCategoryId +
                          "-" +
                          mapping.id
                        }
                        mapping={mapping}
                      />
                    ))}
                  </div>
                )}
              </>
            ) : (
              <div className="pa3 sfondoBiancoTema">
                Seleziona una categoria Prestashop.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrestashopCategoriesPage;
